/* eslint-disable react/jsx-no-bind */
// Disabling JSDoc ESLint rules only because this code is checked by TypeScript directly
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable jsdoc/valid-types */
import React, { useEffect } from 'react';
import getConfig from 'next/config';
import useSWR from 'swr';
import { SCREENS } from 'lib/loginFormConstants';
import { fetcher } from 'lib/jsonFetcher';
import { useBedrockRegistration } from 'store/bedrockRegistration';
import { z } from 'zod';
import { InterestsForm } from './InterestsForm';

const sectionSchema = z.array(
  z.object({
    title: z.string(),
    taxonomyTerm: z.string(),
    image: z.string().catch(() => ''),
  }),
);

const subPrefsSchema = z.object({
  topics: sectionSchema,
  shows: sectionSchema,
  newsletters: z.array(
    z.object({
      title: z.string(),
      description: z.string(),
      image: z.string(),
      newsletterId: z.string(),
    }),
  ),
});

/** @typedef {z.output<typeof subPrefsSchema>} SubscriptionPreferencesResponse */

/** @typedef {Array<{
 *   sectionTitle: string;
 *   sectionItems: Array<{
 *     title: string;
 *     taxonomyTerm: string;
 *     imageUrl: string;
 *   }>;
 * }>} Interests */

const {
  publicRuntimeConfig: { SUBSCRIPTION_PREFERENCES_API_URL },
} = getConfig();

/**
 * Transform the fetched data into the shape expected by InterestsForm
 * @param {SubscriptionPreferencesResponse} data
 * @returns {Interests}
 */
function transformToInterests(data) {
  const sectionOrder = /** @type {const} */ (['topics', 'shows', 'newsletters']);

  /** @type {Interests} */
  return sectionOrder.map((sectionKey) => {
    if (sectionKey === 'newsletters') {
      return {
        sectionTitle: 'Newsletters',
        sectionItems: data.newsletters.map((item) => ({
          title: item.title,
          taxonomyTerm: item.newsletterId,
          imageUrl: item.image,
        })),
      };
    }
    const sectionData = data[sectionKey];
    const sectionTitle = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
    return {
      sectionTitle,
      sectionItems: sectionData.map((item) => ({
        title: item.title,
        taxonomyTerm: item.taxonomyTerm,
        imageUrl: item.image,
      })),
    };
  });
}

/**
 * Select Interests screen component.
 *
 * Handles the high-level logic for fetching and displaying user interests.
 */
export function SelectInterests() {
  const updateContentInterests = useBedrockRegistration((state) => state.updateContentInterests);
  const loading = useBedrockRegistration((state) => state.loading);
  const signInType = useBedrockRegistration((state) => state.signInType);
  const setScreen = useBedrockRegistration((state) => state.setScreen);

  const { data, error } = useSWR(SUBSCRIPTION_PREFERENCES_API_URL, fetcher(subPrefsSchema));

  const hasError = !!error;

  function onNext() {
    setScreen(signInType === 'registration' ? SCREENS.REGISTRATION_SUCCESS : SCREENS.LOGIN_SUCCESS);
  }

  useEffect(() => {
    if (hasError) {
      // eslint-disable-next-line no-console
      console.error('Error loading interests data:', error);
      onNext();
    }
  }, [hasError]);

  if (hasError) {
    return null;
  }

  if (data === undefined) {
    return <div>Loading...</div>;
  }

  return (
    <InterestsForm
      available={transformToInterests(data)}
      onSkip={onNext}
      onSave={(interests) => updateContentInterests(interests).finally(onNext)}
      isSubmitting={loading}
      initialValues={[]}
    />
  );
}
